/**
 * CommunicationsHubV2 — top-level router for the new two-pillar IA.
 *
 * Phase 8 Prompt 6 — per audit 04 §2.1 sidebar IA + §2.4 route map.
 *
 * Mounts at /dashboard/communications/* and dispatches to:
 *   • Overview (default)
 *   • /transactional/* → Transactional pillar surfaces (view-only per HC-3)
 *   • /marketing/* → Marketing & Nurture pillar surfaces (full CRUD)
 *   • /operate/* → Live surveillance
 *   • /measure/* → Analytics
 *
 * 301-redirects from legacy paths handled in App.tsx.
 *
 * Surface components are placeholder skeletons in Prompt 6; Phase 8 Prompts
 * 7–23 fill them in.
 */

import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";

// Overviews
// CommsOverviewPage removed — /communications/overview redirects to the canonical Measure Overview
import EmailOverviewPage from "./overviews/EmailOverviewPage";
import TemplateDetailPage from "./overviews/TemplatePerformancePage";

// Transactional pillar (placeholders — view-only per HC-3)
import TransactionalTemplatesPage from "./transactional/TransactionalTemplatesPage";
import TransactionalTriggersPage from "./transactional/TransactionalTriggersPage";

// Marketing pillar (simplified: Overview · Templates · Audiences · Compose & Send · Triggers)
import ComposeAndSendPage from "./marketing/MarketingCampaignsPage"; // unified Campaigns + Ad-hoc
import MarketingAudiencesPage from "./marketing/MarketingAudiencesPage";
import MarketingAudienceDetailPage from "./marketing/MarketingAudienceDetailPage";
import MarketingAudienceEditPage from "./marketing/MarketingAudienceEditPage";
import MarketingTemplatesPage from "./marketing/MarketingTemplatesPage";
import MarketingTriggersPage from "./marketing/MarketingTriggersPage";

// Operate
import LiveActivityStreamPage from "./operate/LiveActivityStreamPage";
import ScheduledSendsQueuePage from "./operate/ScheduledSendsQueuePage";

// Measure
import MeasureOverviewPage from "./measure/MeasureOverviewPage";
import FunnelPerformancePage from "./measure/FunnelPerformancePage";
import CampaignPerformancePage from "./measure/CampaignPerformancePage";
import AudienceInsightsPage from "./measure/AudienceInsightsPage";
import TemplatePerformancePage from "./measure/TemplatePerformancePage";
import TriggerHealthPage from "./measure/TriggerHealthPage";
import DeliverabilityHealthPage from "./measure/DeliverabilityHealthPage";

/** Carries a legacy /funnels/:slug link into the Funnel Manager's ?flow= param. */
const RedirectToFunnel: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return (
    <Navigate
      to={`/dashboard/communications/marketing/funnels${slug ? `?flow=${encodeURIComponent(slug)}` : ""}`}
      replace
    />
  );
};

const CommunicationsHubV2: React.FC = () => (
  <Routes>
    {/* Default landing */}
    {/* Canonical cross-channel dashboard lives in Measure; the old hub overview redirects there */}
    <Route index element={<Navigate to="measure/overview" replace />} />
    <Route
      path="overview"
      element={<Navigate to="/dashboard/communications/measure/overview" replace />}
    />

    {/* ─── Transactional pillar ─────────────────────────────────────── */}
    <Route
      path="transactional/overview"
      element={<EmailOverviewPage kind="transactional" />}
    />
    {/* Per-template performance (click-through from the overview). Static
        segments outrank the templates/* splat, so this wins for that shape. */}
    <Route
      path="transactional/templates/:key/performance"
      element={<TemplateDetailPage kind="transactional" />}
    />
    <Route
      path="transactional/templates/*"
      element={<TransactionalTemplatesPage />}
    />
    <Route
      path="transactional/triggers/*"
      element={<TransactionalTriggersPage />}
    />

    {/* ─── Marketing & Nurture pillar ───────────────────────────────── */}
    <Route path="marketing/overview" element={<EmailOverviewPage kind="nurture" />} />
    <Route
      path="marketing/templates/:key/performance"
      element={<TemplateDetailPage kind="nurture" />}
    />
    {/* Funnel Manager is mounted in App.tsx (ahead of this hub's splat) so it
        can carry its own guard; it must NOT also be claimed here or the two
        would bounce off each other. */}
    {/* Merged Campaigns + Ad-hoc → one Compose & Send surface (send now / schedule) */}
    <Route path="marketing/compose/*" element={<ComposeAndSendPage />} />
    {/* Legacy /adhoc kept on the same component so ?audience deep-links still work */}
    <Route path="marketing/adhoc/*" element={<ComposeAndSendPage />} />
    <Route
      path="marketing/campaigns/*"
      element={
        <Navigate to="/dashboard/communications/marketing/compose" replace />
      }
    />
    <Route path="marketing/audiences" element={<MarketingAudiencesPage />} />
    <Route
      path="marketing/audiences/:segmentId/edit"
      element={<MarketingAudienceEditPage />}
    />
    <Route
      path="marketing/audiences/:segmentId"
      element={<MarketingAudienceDetailPage />}
    />
    <Route
      path="marketing/templates/*"
      element={<MarketingTemplatesPage />}
    />
    <Route
      path="marketing/triggers/*"
      element={<MarketingTriggersPage />}
    />

    {/* ─── Operate ───────────────────────────────────────────────────── */}
    {/* Free Tier Conversion Engine is now the single 'Conversion & Nurture Engine' entry */}
    <Route
      path="operate/free-tier-engine"
      element={<Navigate to="/dashboard/communications/marketing/funnels" replace />}
    />
    <Route path="operate/live" element={<LiveActivityStreamPage />} />
    <Route path="operate/queue" element={<ScheduledSendsQueuePage />} />

    {/* ─── Measure ───────────────────────────────────────────────────── */}
    <Route path="measure/overview" element={<MeasureOverviewPage />} />
    <Route path="measure/funnels/*" element={<FunnelPerformancePage />} />
    <Route
      path="measure/campaigns/*"
      element={<CampaignPerformancePage />}
    />
    <Route
      path="measure/audiences/*"
      element={<AudienceInsightsPage />}
    />
    <Route
      path="measure/templates/*"
      element={<TemplatePerformancePage />}
    />
    <Route path="measure/triggers/*" element={<TriggerHealthPage />} />
    <Route
      path="measure/deliverability/*"
      element={<DeliverabilityHealthPage />}
    />

    {/* ─── Legacy paths (kept inside the hub for in-app 301s) ───────── */}
    <Route
      path="funnels"
      element={<Navigate to="/dashboard/communications/marketing/funnels" replace />}
    />
    {/* Navigate cannot interpolate a param, so a tiny component carries the
        slug through as ?flow= for the Funnel Manager to pre-expand. */}
    <Route path="funnels/:slug" element={<RedirectToFunnel />} />
    <Route
      path="audience"
      element={<Navigate to="/dashboard/communications/marketing/audiences" replace />}
    />
    <Route
      path="adhoc"
      element={<Navigate to="/dashboard/communications/marketing/compose" replace />}
    />
    <Route
      path="email-hub"
      element={<Navigate to="/dashboard/communications/marketing/templates" replace />}
    />
    <Route
      path="email-marketing"
      element={<Navigate to="/dashboard/communications/marketing/compose" replace />}
    />
    <Route
      path="email-campaigns"
      element={<Navigate to="/dashboard/communications/marketing/compose" replace />}
    />
    <Route
      path="email-templates"
      element={<Navigate to="/dashboard/communications/marketing/templates" replace />}
    />
    <Route
      path="email-triggers"
      element={<Navigate to="/dashboard/communications/marketing/triggers" replace />}
    />
    <Route
      path="flows/new"
      element={<Navigate to="/dashboard/communications/marketing/funnels" replace />}
    />
    <Route
      path="push"
      element={<Navigate to="/dashboard/communications/marketing/compose?channel=push" replace />}
    />
    <Route
      path="sms"
      element={<Navigate to="/dashboard/communications/marketing/compose?channel=sms" replace />}
    />

    {/* Catch-all */}
    <Route path="*" element={<Navigate to="overview" replace />} />
  </Routes>
);

export default CommunicationsHubV2;
